<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
  >
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>

    <div class="kds-content-block">
      <BaseContentCard
        :title="$t('eu__insurance_search_primer__title')"
        :description="$t('eu__insurance_search_primer__description')"
        class="kds-color-background-green-subtle"
      >
        <template #content>
          <div class="steps_grid">
            <CompletedIcon />
            <p class="kds-typography-paragraph-large">
              {{ $t('eu__insurance_search_primer__step_1') }}
            </p>
            <CompletedIcon />
            <p class="kds-typography-paragraph-large">
              {{ $t('eu__insurance_search_primer__step_2') }}
            </p>
            <CompletedIcon />
            <p class="kds-typography-paragraph-large">
              {{ $t('eu__insurance_search_primer__step_3') }}
            </p>
          </div>
        </template>
        <template #illustration>
          <div
            role="img"
            aria-label="Illustration"
            v-html="valueFramingSvg"
          />
        </template>
      </BaseContentCard>
      <DocumentViewer
        v-if="!consentAlreadyGiven"
        class="kds-typography-paragraph-small"
        :link="consentDescription"
      />
    </div>
    <template #actions>
      <BaseButton
        data-qa="submit"
        @click="navigateToNext"
      >
        {{ $t('generic_action_continue') }}
      </BaseButton>
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import BaseContentCard from '@shared/components/BaseContentCard.vue'
import CompletedIcon from '@shared/assets/icon/CompletedIcon.vue'
import valueFramingSvg from '@eu/app/assets/img/value_framing.svg?raw'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import * as apiRequest from '@shared/api-endpoints/index.js'
import DocumentViewer from '@shared/components/DocumentViewer.vue'
import { computed } from 'vue'
import env from '@shared/env.js'
import defineLanguage from '@shared/utils/defineLanguage.js'
import useUserStore from '@shared/stores/useUserStore.js'
import { useI18n } from 'petite-vue-i18n'

const { t } = useI18n()
const userStore = useUserStore()
const { navigateNext, navigateBack, canGoBack } = useNavigation(
  ROUTE.INSURANCE_SEARCH_PRIMER,
)
const { progress } = useOnboardingProgress()

// Snapshot consent state at view load so the DocumentViewer stays visible
// after saving a fresh consent (it should only be hidden for returning users).
const consentAlreadyGiven = userStore.user?.consents?.web_health_data === true

const consentDescription = computed(() => {
  const privacyLink = `${env.VITE_PUBLIC_PATH}static/${defineLanguage(userStore.lang)}/privacy-website/index.html`
  return t('eu__insurance_search_primer__web_health_data_consent', {
    privacyLink,
  })
})

const navigateToNext = async () => {
  if (!consentAlreadyGiven) {
    try {
      await apiRequest.updateUserConsents([
        { type: 'web_health_data', given: true },
      ])
    } catch {
      // consent recording is non-critical; proceed regardless
    }
  }

  navigateNext()
}
</script>

<style lang="scss" scoped>
.steps_grid {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: $kds-space-2x $kds-space-1x;
  align-items: center;
}
</style>
